import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};

const WEEKDAYS = [
  { value: 0, label: "Esmaspäev" },
  { value: 1, label: "Teisipäev" },
  { value: 2, label: "Kolmapäev" },
  { value: 3, label: "Neljapäev" },
  { value: 4, label: "Reede" },
  { value: 5, label: "Laupäev" },
  { value: 6, label: "Pühapäev" },
];

export default function DoctorAvailability() {
  const [vacationRanges, setVacationRanges] = useState([]);
  const [weeklyFreeDays, setWeeklyFreeDays] = useState([5, 6]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/doctor-availability`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setVacationRanges(data.vacation_ranges || []);
        setWeeklyFreeDays(data.weekly_free_days || [5, 6]);
      }
    } catch {
      /* ignore */
    }
    setLoaded(true);
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/doctor-availability`, {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify({
          vacation_ranges: vacationRanges.filter(v => v.start_date && v.end_date),
          weekly_free_days: weeklyFreeDays,
        }),
      });
      if (res.ok) {
        toast.success("Saadavus salvestatud");
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Salvestamine ebaõnnestus");
      }
    } catch {
      toast.error("Võrguühenduse viga");
    }
    setSaving(false);
  };

  const toggleFreeDay = (day) => {
    setWeeklyFreeDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const addVacation = () => {
    setVacationRanges(prev => [...prev, { start_date: "", end_date: "" }]);
  };

  const updateVacation = (idx, field, value) => {
    setVacationRanges(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  const removeVacation = (idx) => {
    setVacationRanges(prev => prev.filter((_, i) => i !== idx));
  };

  if (!loaded) return null;

  return (
    <Card className="border-slate-200" data-testid="doctor-availability-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-sky-600" />
          Arsti saadavus ja emaili teavitused
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Emaili teavitused saadetakse ainult siis, kui olete puhkusel või vabal päeval.
          Tööpäevadel näete teavitusi otse rakenduses.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Weekly free days */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Iganädalased vabad päevad</h3>
          <p className="text-xs text-slate-400 mb-3">Nendel päevadel saadetakse emailid automaatselt</p>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => toggleFreeDay(value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  weeklyFreeDays.includes(value)
                    ? "bg-sky-100 text-sky-700 border-2 border-sky-300"
                    : "bg-slate-100 text-slate-500 border-2 border-transparent hover:border-slate-300"
                }`}
                data-testid={`free-day-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Vacation ranges */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Puhkuseperioodid</h3>
              <p className="text-xs text-slate-400">Nendel kuupäevadel saadetakse emailid automaatselt</p>
            </div>
            <Button size="sm" variant="outline" onClick={addVacation} data-testid="add-vacation-btn">
              <Plus className="w-4 h-4 mr-1" /> Lisa periood
            </Button>
          </div>
          {vacationRanges.length === 0 ? (
            <p className="text-sm text-slate-400 italic">Puhkuseperioode pole lisatud</p>
          ) : (
            <div className="space-y-2">
              {vacationRanges.map((vac, idx) => {
                const isActive = vac.start_date && vac.end_date &&
                  new Date().toISOString().slice(0, 10) >= vac.start_date &&
                  new Date().toISOString().slice(0, 10) <= vac.end_date;
                return (
                  <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg ${isActive ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`} data-testid={`vacation-row-${idx}`}>
                    <input
                      type="date"
                      value={vac.start_date}
                      onChange={e => updateVacation(idx, "start_date", e.target.value)}
                      className="border border-slate-200 rounded px-2 py-1 text-sm"
                      data-testid={`vacation-start-${idx}`}
                    />
                    <span className="text-slate-400 text-sm">—</span>
                    <input
                      type="date"
                      value={vac.end_date}
                      onChange={e => updateVacation(idx, "end_date", e.target.value)}
                      className="border border-slate-200 rounded px-2 py-1 text-sm"
                      data-testid={`vacation-end-${idx}`}
                    />
                    {isActive && <Badge className="bg-amber-100 text-amber-700 text-xs">Aktiivne</Badge>}
                    <button
                      onClick={() => removeVacation(idx)}
                      className="p-1 rounded hover:bg-red-50 ml-auto"
                      data-testid={`vacation-delete-${idx}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Save */}
        <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-availability-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Salvestab..." : "Salvesta saadavus"}
        </Button>
      </CardContent>
    </Card>
  );
}
